import api from './api'

import type {
  CardBlock,
  ScriptUnit,
  TCardBlockValue,
  ScriptCardResponse,
  ScriptContinueResponse,
  ScriptMoveResponse,
  ScriptStartOrBackResponse,
  DataTableResponse,
  HistoryItem,
  DownloadLink,
  CreateCardBlockRequest,
  CreateScriptResponse,
} from '@/models/scripts'
import type { RecordingLoadOncePayload } from '@/store/modules/recordings/actions'
import type { CardVariant } from '@/constants/scripts'

class ScriptsApiService {
  static getInstance(): ScriptsApiService {
    return this.instance || (this.instance = new this())
  }

  private static instance: ScriptsApiService

  private baseUrl = '/scripts/'

  private constructor() {
    // empty for linter
  }

  create(title: string) {
    const url = `${this.baseUrl}create`

    return api.post<CreateScriptResponse>(url, { title })
  }

  update({ id, title }: { id: string; title: string }) {
    const url = `${this.baseUrl}${id}`

    return api.post<{ id: string }>(url, { title })
  }

  getScript(
    scriptId: string,
    options?: { withTests: boolean; withVersions: boolean },
  ) {
    const url = `/scripts/${scriptId}`
    const { withTests, withVersions } = options || {}

    return api.get<ScriptUnit>(url, {
      params: {
        expand: ['instance']
          .concat(withTests ? 'tests' : [])
          .concat(withVersions ? 'versions' : [])
          .join(','),
      },
    })
  }

  getScripts(options?: { withTests: boolean; withVersions: boolean }) {
    const { withTests, withVersions } = options || {}

    return api.get<ScriptUnit[]>(this.baseUrl, {
      params: {
        expand: ['instance']
          .concat(withTests ? 'tests' : [])
          .concat(withVersions ? 'versions' : [])
          .join(','),
      },
    })
  }

  usageStart(scriptId: string) {
    const url = `${this.baseUrl}${scriptId}/usage/start`

    return api.get<ScriptStartOrBackResponse>(url)
  }

  usageContinue(scriptId: string) {
    const url = `${this.baseUrl}${scriptId}/usage/continue`

    return api.get<ScriptContinueResponse>(url)
  }

  usageFinish(scriptId: string, reason: string) {
    const url = `${this.baseUrl}${scriptId}/usage/finish`

    return api.post<boolean>(url, { reason })
  }

  usageMove(scriptId: string, options: { from: number; by: number }) {
    const url = `${this.baseUrl}${scriptId}/usage/move`

    const { from, by } = options

    return api.get<ScriptMoveResponse>(url, {
      params: {
        ['from_step']: from,
        ['by_option']: by,
      },
    })
  }

  usageBack(scriptId: string, options: { to: number }) {
    const url = `${this.baseUrl}${scriptId}/usage/back`

    return api.get<ScriptStartOrBackResponse>(url, { params: options })
  }

  getScriptCardContent(
    scriptId: string,
    cardNumOrId: string | 'first',
    instance?: string,
  ) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardNumOrId}`

    return api.get<ScriptCardResponse>(url, {
      params: { expand: 'content', instance },
    })
  }

  updateScriptCard(scriptId: string, cardId: string, name: string) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}`

    return api.post<boolean>(url, { name })
  }

  createTest(scriptId: string, cardId: string) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}/test`

    return api.post<boolean>(url)
  }

  deleteTest(scriptId: string, cardId: string, variant: CardVariant) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}/test`

    return api.delete<boolean>(url, { params: { variant } })
  }

  createBlock(
    scriptId: string,
    cardId: string,
    data: CreateCardBlockRequest,
  ) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}/blocks`

    return api.post<CardBlock>(url, data)
  }

  editBlock(
    scriptId: string,
    cardId: string,
    blockId: string,
    value: TCardBlockValue,
  ) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}/blocks/${blockId}`

    return api.post<boolean>(url, value)
  }

  deleteBlock(scriptId: string, cardId: string, blockId: string) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}/blocks/${blockId}`

    return api.delete<boolean>(url)
  }

  deleteOption(scriptId: string, cardId: string, optionId: string) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}/options/${optionId}`

    return api.delete<boolean>(url)
  }

  editBlockNumber(
    scriptId: string,
    cardId: string,
    blockId: string,
    num: number,
  ) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}/blocks/${blockId}`

    return api.post<boolean>(url, { num })
  }

  editOptionNumber(
    scriptId: string,
    cardId: string,
    optionId: string,
    num: number,
  ) {
    const url = `${this.baseUrl}${scriptId}/cards/${cardId}/options/${optionId}`

    return api.post<boolean>(url, { num })
  }

  getRecordings(
    limit: number,
    offset: number,
    expand: string,
    sort: string,
    filter: string,
  ) {
    let expandWithId = expand
    if (!expand.split(',').includes('id') && Object.keys(expand).length !== 0) {
      const arr = expand.split(',')
      arr.unshift('id')
      expandWithId = arr.join()
    }
    const url = `${this.baseUrl}recordings?expand=${expandWithId}&offset=${offset}&limit=${limit}&sort=${sort}&filter=${filter}`
    return api.get<DataTableResponse>(url)
  }
  downloadRecordings(id: string[], exept: boolean) {
    const stringIds = id.join()
    const url = `${this.baseUrl}recordings/export?ids=${stringIds}&except=${exept}`
    return api.get<DownloadLink>(url)
  }

  getRecordingItem(data: RecordingLoadOncePayload) {
    const url = `${this.baseUrl}recordings?expand=${data.expand}&filter=id:E${data.id}`
    return api.get<DataTableResponse>(url)
  }

  getRecordingHistory(recordId: string) {
    const url = `${this.baseUrl}recordings/${recordId}/history`

    return api.get<HistoryItem[]>(url)
  }

  getActiveScriptRecordingHistory() {
    const url = `${this.baseUrl}recordings/active/history`

    return api.get<HistoryItem[]>(url)
  }
}

export default ScriptsApiService.getInstance()
